import React, { useState } from 'react';
import { 
  TrendingUp, 
  TrendingDown, 
  AlertTriangle, 
  Zap, 
  Activity,
  ArrowLeft,
  Calendar,
  Target,
  BarChart3,
  Users,
  DollarSign,
  Globe,
  Lightbulb,
  Shield,
  Filter,
  Search,
  ExternalLink
} from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, AreaChart, Area, BarChart, Bar } from 'recharts';
import { useNavigate } from 'react-router-dom';
import MarketInsightDetailsModal from '../components/MarketInsightDetailsModal';
import CreateBacklogItemModal from '../components/CreateBacklogItemModal';

const MarketInsights: React.FC = () => {
  const navigate = useNavigate();
  const [activeFilter, setActiveFilter] = useState<string>('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedInsight, setSelectedInsight] = useState<any>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isBacklogModalOpen, setIsBacklogModalOpen] = useState(false);
  const [selectedInsightForBacklog, setSelectedInsightForBacklog] = useState<any>(null);

  // Market trend data
  const marketTrendData = [
    { month: 'Jan', opportunity: 65, threat: 35, growth: 12 },
    { month: 'Feb', opportunity: 72, threat: 28, growth: 18 },
    { month: 'Mar', opportunity: 68, threat: 42, growth: 15 },
    { month: 'Apr', opportunity: 78, threat: 25, growth: 22 },
    { month: 'May', opportunity: 85, threat: 30, growth: 28 },
    { month: 'Jun', opportunity: 92, threat: 22, growth: 35 },
  ];

  // Opportunity scoring data - Fixed for proper rendering with shorter names
  const opportunityData = [
    { name: 'AI Personalisation', score: 95, impact: 'High', timeframe: 'Q2 2024', investment: '£2.5M' },
    { name: 'Mobile Commerce', score: 88, impact: 'High', timeframe: 'Q1 2024', investment: '£1.8M' },
    { name: 'Voice Interface', score: 76, impact: 'Medium', timeframe: 'Q3 2024', investment: '£3.2M' },
    { name: 'AR Shopping', score: 65, impact: 'Medium', timeframe: 'Q4 2024', investment: '£4.1M' },
    { name: 'Social Commerce', score: 58, impact: 'Low', timeframe: 'Q1 2025', investment: '£1.2M' },
  ];

  // Detailed insights data
  const detailedInsights = [
    {
      id: 1,
      type: 'opportunity',
      category: 'Technology',
      title: 'AI-Powered Personalisation Engine',
      description: 'Machine learning algorithms showing 23% engagement increase across user segments',
      impact: 'high',
      confidence: 92,
      timeframe: '3-6 months',
      investment: '£2.5M',
      roi: '340%',
      signals: [
        'Customer engagement up 23%',
        'Conversion rates improved 18%',
        'User session time +35%',
        'Competitor adoption accelerating'
      ],
      recommendations: [
        'Accelerate ML model development',
        'Expand A/B testing scope',
        'Secure additional data science talent',
        'Partner with AI technology vendors'
      ],
      risks: ['Data privacy concerns', 'Technical complexity', 'Resource allocation'],
      lastUpdated: '2 hours ago'
    },
    {
      id: 2,
      type: 'growth',
      category: 'Market',
      title: 'Mobile-First Demographics Surge',
      description: 'Target demographic showing 45% increase in mobile usage and purchasing behaviour',
      impact: 'high',
      confidence: 88,
      timeframe: '1-3 months',
      investment: '£1.8M',
      roi: '280%',
      signals: [
        'Mobile traffic up 45%',
        'Mobile conversion +32%',
        'App downloads increased 67%',
        'Mobile payment adoption rising'
      ],
      recommendations: [
        'Prioritise mobile UX optimisation',
        'Expand mobile payment options',
        'Develop progressive web app',
        'Increase mobile marketing spend'
      ],
      risks: ['Platform fragmentation', 'Performance optimisation', 'User experience consistency'],
      lastUpdated: '4 hours ago'
    },
    {
      id: 3,
      type: 'threat',
      category: 'Competitive',
      title: 'Competitor Pricing Strategy Shift',
      description: 'Major competitor implementing aggressive pricing strategy in key market segments',
      impact: 'high',
      confidence: 85,
      timeframe: 'Immediate',
      investment: 'TBD',
      roi: 'Risk Mitigation',
      signals: [
        'Competitor prices down 15%',
        'Market share pressure detected',
        'Customer price sensitivity rising',
        'Margin compression risk'
      ],
      recommendations: [
        'Analyse value proposition differentiation',
        'Consider strategic pricing adjustments',
        'Enhance customer loyalty programmes',
        'Focus on premium service offerings'
      ],
      risks: ['Margin erosion', 'Price war escalation', 'Brand positioning impact'],
      lastUpdated: '1 hour ago'
    },
    {
      id: 4,
      type: 'opportunity',
      category: 'Innovation',
      title: 'Voice Commerce Integration',
      description: 'Voice interface technology showing strong adoption signals in target markets',
      impact: 'medium',
      confidence: 76,
      timeframe: '6-12 months',
      investment: '£3.2M',
      roi: '220%',
      signals: [
        'Voice search queries +58%',
        'Smart speaker adoption rising',
        'Voice commerce transactions up 89%',
        'Accessibility demand increasing'
      ],
      recommendations: [
        'Develop voice interface prototype',
        'Partner with voice technology providers',
        'Conduct user experience research',
        'Plan phased rollout strategy'
      ],
      risks: ['Technology maturity', 'User adoption uncertainty', 'Privacy concerns'],
      lastUpdated: '6 hours ago'
    },
    {
      id: 5,
      type: 'growth',
      category: 'Geographic',
      title: 'Emerging Market Expansion',
      description: 'Southeast Asian markets showing strong growth potential and market entry opportunities',
      impact: 'medium',
      confidence: 71,
      timeframe: '9-18 months',
      investment: '£5.8M',
      roi: '195%',
      signals: [
        'GDP growth rates 6-8%',
        'Digital adoption accelerating',
        'Regulatory environment improving',
        'Local partnership opportunities'
      ],
      recommendations: [
        'Conduct detailed market research',
        'Identify local partnership opportunities',
        'Develop localisation strategy',
        'Assess regulatory requirements'
      ],
      risks: ['Regulatory changes', 'Cultural adaptation', 'Currency fluctuation'],
      lastUpdated: '1 day ago'
    },
    {
      id: 6,
      type: 'threat',
      category: 'Regulatory',
      title: 'Data Privacy Regulation Changes',
      description: 'New data privacy regulations may impact current data collection and processing practices',
      impact: 'medium',
      confidence: 82,
      timeframe: '3-6 months',
      investment: '£1.5M',
      roi: 'Compliance',
      signals: [
        'New regulations proposed',
        'Industry compliance costs rising',
        'Customer privacy concerns increasing',
        'Regulatory enforcement strengthening'
      ],
      recommendations: [
        'Audit current data practices',
        'Implement privacy-by-design principles',
        'Enhance consent management systems',
        'Train teams on compliance requirements'
      ],
      risks: ['Non-compliance penalties', 'Operational disruption', 'Competitive disadvantage'],
      lastUpdated: '3 hours ago'
    }
  ];

  const getTypeColor = (type: string) => {
    switch (type) {
      case 'opportunity': return 'text-green-400 bg-green-900 border-green-600';
      case 'growth': return 'text-blue-400 bg-blue-900 border-blue-600';
      case 'threat': return 'text-red-400 bg-red-900 border-red-600';
      default: return 'text-gray-400 bg-gray-900 border-gray-600';
    }
  };

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'opportunity': return <Lightbulb className="w-4 h-4" />;
      case 'growth': return <TrendingUp className="w-4 h-4" />;
      case 'threat': return <AlertTriangle className="w-4 h-4" />;
      default: return <Activity className="w-4 h-4" />;
    }
  };

  const getImpactColor = (impact: string) => {
    switch (impact) {
      case 'high': return 'text-red-400';
      case 'medium': return 'text-yellow-400';
      case 'low': return 'text-green-400';
      default: return 'text-gray-400';
    }
  };

  const getCategoryIcon = (category: string) => {
    switch (category) {
      case 'Technology': return <Zap className="w-4 h-4" />;
      case 'Market': return <BarChart3 className="w-4 h-4" />;
      case 'Competitive': return <Target className="w-4 h-4" />;
      case 'Innovation': return <Lightbulb className="w-4 h-4" />;
      case 'Geographic': return <Globe className="w-4 h-4" />;
      case 'Regulatory': return <Shield className="w-4 h-4" />;
      default: return <Activity className="w-4 h-4" />;
    }
  };

  const filteredInsights = detailedInsights.filter(insight => {
    const matchesFilter = activeFilter === 'all' || insight.type === activeFilter;
    const matchesSearch = insight.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         insight.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         insight.category.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesFilter && matchesSearch;
  });

  const typeCounts = {
    all: detailedInsights.length,
    opportunity: detailedInsights.filter(i => i.type === 'opportunity').length,
    growth: detailedInsights.filter(i => i.type === 'growth').length,
    threat: detailedInsights.filter(i => i.type === 'threat').length,
  };

  const handleViewDetails = (insight: any) => {
    setSelectedInsight(insight);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedInsight(null);
  };

  const handleAddToBacklog = (insight: any) => {
    setSelectedInsightForBacklog(insight);
    setIsBacklogModalOpen(true);
  };

  const handleCloseBacklogModal = () => {
    setIsBacklogModalOpen(false);
    setSelectedInsightForBacklog(null);
  };

  const handleCreateBacklogItem = (backlogData: any) => {
    // Dispatch event to notify backlog page
    const event = new CustomEvent('new-backlog-item-created', { 
      detail: backlogData 
    });
    window.dispatchEvent(event);
    
    // Show success notification
    const notification = document.createElement('div');
    notification.className = 'fixed top-4 right-4 bg-green-600 text-white px-6 py-3 rounded-lg shadow-lg z-50';
    notification.innerHTML = `
      <div class="flex items-center space-x-2">
        <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
          <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd"></path>
        </svg>
        <span>Backlog item "${backlogData.title}" created successfully!</span>
      </div>
    `;
    document.body.appendChild(notification);
    
    setTimeout(() => {
      if (document.body.contains(notification)) {
        document.body.removeChild(notification);
      }
    }, 5000);
    
    setIsBacklogModalOpen(false);
  };

  // Custom tooltip for opportunity chart
  const OpportunityTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div className="bg-gray-900 border border-gray-600 rounded-lg p-3 shadow-xl">
          <p className="text-white font-medium mb-2">{data.name}</p>
          <p className="text-indigo-400 text-sm">Score: {data.score}</p>
          <p className="text-gray-400 text-sm">Impact: {data.impact}</p>
          <p className="text-gray-400 text-sm">Timeline: {data.timeframe}</p>
          <p className="text-green-400 text-sm">Investment: {data.investment}</p>
        </div>
      );
    }
    return null;
  };

  return (
    <div className="space-y-4 lg:space-y-6">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between">
        <div className="flex items-center space-x-4">
          <button
            onClick={() => navigate('/')}
            className="text-gray-400 hover:text-white transition-colours"
          >
            <ArrowLeft className="w-5 h-5 lg:w-6 lg:h-6" />
          </button>
          <div>
            <h1 className="text-2xl lg:text-3xl font-bold text-white mb-2">Market Insights & Opportunities</h1>
            <p className="text-gray-400 text-sm lg:text-base">Comprehensive market analysis and strategic opportunity identification</p>
          </div>
        </div>
        <div className="flex flex-col sm:flex-row items-start sm:items-center space-y-2 sm:space-y-0 sm:space-x-3 mt-4 lg:mt-0">
          <div className="flex items-center space-x-2">
            <div className="w-3 h-3 bg-green-500 rounded-full animate-pulse"></div>
            <span className="text-sm text-gray-300">Live Market Feed</span>
          </div>
          <button className="bg-indigo-600 hover:bg-indigo-700 text-white px-3 lg:px-4 py-2 rounded-lg transition-colours flex items-center space-x-2">
            <ExternalLink className="w-4 h-4" />
            <span>Export Report</span>
          </button>
        </div>
      </div>

      {/* Overview Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4 lg:gap-6">
        <div className="bg-gray-800 rounded-lg p-4 lg:p-6 border border-gray-700">
          <div className="flex items-center justify-between mb-3 lg:mb-4">
            <h3 className="text-sm font-medium text-gray-300">Total Opportunities</h3>
            <Lightbulb className="w-4 h-4 lg:w-5 lg:h-5 text-green-500" />
          </div>
          <div className="text-xl lg:text-2xl font-bold text-white">12</div>
          <div className="text-sm text-green-500">+3 this week</div>
        </div>
        <div className="bg-gray-800 rounded-lg p-4 lg:p-6 border border-gray-700">
          <div className="flex items-center justify-between mb-3 lg:mb-4">
            <h3 className="text-sm font-medium text-gray-300">Growth Signals</h3>
            <TrendingUp className="w-4 h-4 lg:w-5 lg:h-5 text-blue-500" />
          </div>
          <div className="text-xl lg:text-2xl font-bold text-white">8</div>
          <div className="text-sm text-blue-500">+2 this week</div>
        </div>
        <div className="bg-gray-800 rounded-lg p-4 lg:p-6 border border-gray-700">
          <div className="flex items-center justify-between mb-3 lg:mb-4">
            <h3 className="text-sm font-medium text-gray-300">Market Threats</h3>
            <AlertTriangle className="w-4 h-4 lg:w-5 lg:h-5 text-red-500" />
          </div>
          <div className="text-xl lg:text-2xl font-bold text-white">4</div>
          <div className="text-sm text-red-500">+1 this week</div>
        </div>
        <div className="bg-gray-800 rounded-lg p-4 lg:p-6 border border-gray-700">
          <div className="flex items-center justify-between mb-3 lg:mb-4">
            <h3 className="text-sm font-medium text-gray-300">Avg Confidence</h3>
            <Target className="w-4 h-4 lg:w-5 lg:h-5 text-indigo-500" />
          </div>
          <div className="text-xl lg:text-2xl font-bold text-white">82%</div>
          <div className="text-sm text-indigo-500">+5% this month</div>
        </div>
      </div>

      {/* Charts Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 lg:gap-6">
        {/* Market Trend Analysis */}
        <div className="bg-gray-800 rounded-lg p-4 lg:p-6 border border-gray-700">
          <h3 className="text-base lg:text-lg font-semibold text-white mb-3 lg:mb-4">Market Trend Analysis</h3>
          <ResponsiveContainer width="100%" height={300}>
            <AreaChart data={marketTrendData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
              <XAxis 
                dataKey="month" 
                stroke="#9CA3AF" 
                fontSize={12}
                tick={{ fontSize: 12 }}
              />
              <YAxis 
                stroke="#9CA3AF" 
                fontSize={12}
                tick={{ fontSize: 12 }}
              />
              <Tooltip
                contentStyle={{
                  backgroundColor: '#1F2937',
                  border: '1px solid #374151',
                  borderRadius: '8px',
                  color: '#F9FAFB',
                  fontSize: '12px'
                }}
              />
              <Area type="monotone" dataKey="opportunity" stackId="1" stroke="#10B981" fill="#10B981" fillOpacity={0.6} />
              <Area type="monotone" dataKey="threat" stackId="1" stroke="#EF4444" fill="#EF4444" fillOpacity={0.6} />
            </AreaChart>
          </ResponsiveContainer>
        </div>

        {/* Opportunity Scoring - Fixed Chart with Increased Width */}
        <div className="bg-gray-800 rounded-lg p-4 lg:p-6 border border-gray-700">
          <h3 className="text-base lg:text-lg font-semibold text-white mb-3 lg:mb-4">Opportunity Scoring</h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart 
              data={opportunityData} 
              layout="vertical"
              margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
              <XAxis 
                type="number" 
                stroke="#9CA3AF" 
                fontSize={10}
                tick={{ fontSize: 10 }}
                domain={[0, 100]}
              />
              <YAxis 
                dataKey="name" 
                type="category" 
                stroke="#9CA3AF" 
                width={100}
                fontSize={9}
                tick={{ fontSize: 9 }}
                interval={0}
                tickFormatter={(value) => {
                  // Truncate long names for better display
                  if (value.length > 12) {
                    return value.substring(0, 12) + '...';
                  }
                  return value;
                }}
              />
              <Tooltip content={<OpportunityTooltip />} />
              <Bar dataKey="score" fill="#4F46E5" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Filter Controls */}
      <div className="flex flex-col lg:flex-row gap-3 lg:gap-4">
        {/* Type Filters */}
        <div className="flex flex-wrap gap-2">
          {Object.entries(typeCounts).map(([type, count]) => (
            <button
              key={type}
              onClick={() => setActiveFilter(type)}
              className={`px-3 lg:px-4 py-2 rounded-lg text-sm font-medium transition-colours ${
                activeFilter === type
                  ? 'bg-indigo-600 text-white'
                  : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
              }`}
            >
              {type.charAt(0).toUpperCase() + type.slice(1)} ({count})
            </button>
          ))}
        </div>

        {/* Search */}
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
          <input
            type="text"
            placeholder="Search insights..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 bg-gray-800 text-white rounded-lg border border-gray-700 focus:border-indigo-500 focus:outline-none text-sm lg:text-base"
          />
        </div>
      </div>

      {/* Detailed Insights */}
      <div className="space-y-4 lg:space-y-6">
        {filteredInsights.map((insight) => (
          <div key={insight.id} className="bg-gray-800 rounded-lg p-4 lg:p-6 border border-gray-700 hover:border-gray-600 transition-colours">
            {/* Header */}
            <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between mb-4">
              <div className="flex-1">
                <div className="flex flex-wrap items-center gap-2 lg:gap-3 mb-2">
                  <div className={`flex items-center space-x-1 px-2 py-1 rounded-full text-xs border ${getTypeColor(insight.type)}`}>
                    {getTypeIcon(insight.type)}
                    <span className="capitalize">{insight.type}</span>
                  </div>
                  <div className="flex items-center space-x-1 text-gray-400">
                    {getCategoryIcon(insight.category)}
                    <span className="text-xs">{insight.category}</span>
                  </div>
                  <div className="flex items-center space-x-1 text-gray-500">
                    <Calendar className="w-3 h-3" />
                    <span className="text-xs">{insight.lastUpdated}</span>
                  </div>
                </div>
                <h3 className="text-lg lg:text-xl font-semibold text-white mb-2">{insight.title}</h3>
                <p className="text-gray-400 text-sm lg:text-base">{insight.description}</p>
              </div>
              <div className="flex lg:flex-col gap-4 lg:gap-2 mt-4 lg:mt-0 lg:w-48">
                <div className="text-center">
                  <div className="text-xs text-gray-400">Confidence</div>
                  <div className="text-lg font-bold text-white">{insight.confidence}%</div>
                </div>
                <div className="text-center">
                  <div className="text-xs text-gray-400">Impact</div>
                  <div className={`text-sm font-medium capitalize ${getImpactColor(insight.impact)}`}>
                    {insight.impact}
                  </div>
                </div>
                <div className="text-center">
                  <div className="text-xs text-gray-400">ROI</div>
                  <div className="text-sm font-bold text-green-400">{insight.roi}</div>
                </div>
              </div>
            </div>

            {/* Metrics */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 lg:gap-4 mb-4 lg:mb-6">
              <div className="bg-gray-700 rounded-lg p-3">
                <div className="text-xs text-gray-400">Timeframe</div>
                <div className="text-sm font-medium text-white">{insight.timeframe}</div>
              </div>
              <div className="bg-gray-700 rounded-lg p-3">
                <div className="text-xs text-gray-400">Investment</div>
                <div className="text-sm font-medium text-white">{insight.investment}</div>
              </div>
              <div className="bg-gray-700 rounded-lg p-3">
                <div className="text-xs text-gray-400">Signals</div>
                <div className="text-sm font-medium text-white">{insight.signals.length}</div>
              </div>
              <div className="bg-gray-700 rounded-lg p-3">
                <div className="text-xs text-gray-400">Risks</div>
                <div className="text-sm font-medium text-white">{insight.risks.length}</div>
              </div>
            </div>

            {/* Details Grid */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 lg:gap-6">
              {/* Key Signals */}
              <div>
                <h4 className="text-sm font-medium text-gray-300 mb-3">Key Signals</h4>
                <div className="space-y-2">
                  {insight.signals.map((signal, index) => (
                    <div key={index} className="flex items-center space-x-2">
                      <div className="w-2 h-2 bg-green-500 rounded-full flex-shrink-0"></div>
                      <span className="text-sm text-gray-400">{signal}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Recommendations */}
              <div>
                <h4 className="text-sm font-medium text-gray-300 mb-3">Recommendations</h4>
                <div className="space-y-2">
                  {insight.recommendations.map((rec, index) => (
                    <div key={index} className="flex items-center space-x-2">
                      <div className="w-2 h-2 bg-indigo-500 rounded-full flex-shrink-0"></div>
                      <span className="text-sm text-gray-400">{rec}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Risk Factors */}
              <div>
                <h4 className="text-sm font-medium text-gray-300 mb-3">Risk Factors</h4>
                <div className="space-y-2">
                  {insight.risks.map((risk, index) => (
                    <div key={index} className="flex items-center space-x-2">
                      <div className="w-2 h-2 bg-red-500 rounded-full flex-shrink-0"></div>
                      <span className="text-sm text-gray-400">{risk}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between pt-4 mt-4 lg:mt-6 border-t border-gray-700 gap-3 sm:gap-0">
              <div className="flex flex-wrap items-center gap-4">
                <span className="text-xs text-gray-500">ID: {insight.id}</span>
                <span className="text-xs text-gray-500">Updated: {insight.lastUpdated}</span>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <button className="text-indigo-400 hover:text-indigo-300 transition-colours text-sm">
                  Create Experiment
                </button>
                <button 
                  onClick={() => handleAddToBacklog(insight)}
                  className="text-gray-400 hover:text-gray-300 transition-colours text-sm"
                >
                  Add to Backlog
                </button>
                <button 
                  onClick={() => handleViewDetails(insight)}
                  className="bg-indigo-600 hover:bg-indigo-700 text-white px-3 py-1 rounded text-sm transition-colours"
                >
                  View Details
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {filteredInsights.length === 0 && (
        <div className="text-center py-12">
          <Activity className="w-12 h-12 text-gray-500 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-300 mb-2">No insights found</h3>
          <p className="text-gray-500">Try adjusting your search or filter criteria</p>
        </div>
      )}

      {/* Market Insight Details Modal */}
      <MarketInsightDetailsModal
        insight={selectedInsight}
        isOpen={isModalOpen}
        onClose={handleCloseModal}
      />

      {/* Create Backlog Item Modal */}
      <CreateBacklogItemModal
        insight={selectedInsightForBacklog}
        isOpen={isBacklogModalOpen}
        onClose={handleCloseBacklogModal}
        onCreateBacklogItem={handleCreateBacklogItem}
      />
    </div>
  );
};

export default MarketInsights;